<script>
import axios from 'axios'
import {
  formatDate,
  formatMetric,
  formatMetricCompact,
  riskVariant,
  windowLabel
} from './tops_formatters'

export default {
  props: {
    baseUrl: {
      type: String,
      default: 'http://localhost:5000'
    },
    basePath: {
      type: String,
      default: '/monitoring/tops'
    },
    report: {
      type: String,
      default: ''
    },
    preset: {
      type: Object,
      default: () => ({ metrics: [], extras: [], sets: [], group_by: [] })
    },
    item: {
      type: Object,
      default: () => ({})
    },
    site: {
      type: String,
      default: ''
    },
    device: {
      type: String,
      default: ''
    },
    hours: {
      type: Number,
      default: 24
    },
    metric: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      trend: {},
      metricField: this.metric,
      isBusy: false,
      loaded: false,
      loadError: ''
    }
  },
  computed: {
    metricInfo () {
      return this.preset.metrics.find((entry) => entry.field === this.metricField) || this.preset.metrics[0] || {}
    },
    points () {
      return this.trend.points || []
    },
    presentPoints () {
      return this.points.filter((point) => point.value !== null)
    },
    series () {
      return [
        {
          name: this.metricInfo.label || 'Value',
          type: 'column',
          data: this.points.map((point) => [Date.parse(point.ts), point.value])
        },
        {
          name: 'Rank',
          type: 'line',
          data: this.points.map((point) => [Date.parse(point.ts), point.rank])
        }
      ]
    },
    options () {
      const unit = this.metricInfo.unit
      return {
        chart: { type: 'line', toolbar: { show: false }, animations: { enabled: false } },
        stroke: { width: [0, 2], curve: 'straight' },
        colors: ['#727cf5', '#fa5c7c'],
        dataLabels: { enabled: false },
        markers: { size: [0, 3] },
        // ApexCharts renders datetime axes in UTC unless told otherwise.
        xaxis: { type: 'datetime', labels: { datetimeUTC: false } },
        yaxis: [
          {
            title: { text: this.metricInfo.label },
            labels: { formatter: (value) => formatMetricCompact(value, unit) },
            min: 0
          },
          {
            opposite: true,
            reversed: true,
            title: { text: 'Rank (1 = top)' },
            min: 1,
            max: 25,
            tickAmount: 4,
            labels: { formatter: (value) => (value === null ? '' : Math.round(value)) }
          }
        ],
        tooltip: {
          shared: true,
          x: { format: 'dd MMM HH:mm' },
          y: [
            { formatter: (value) => (value === null || value === undefined ? 'not in top 25' : formatMetric(value, unit)) },
            { formatter: (value) => (value === null || value === undefined ? '—' : `#${value}`) }
          ]
        },
        legend: { show: true, position: 'top' }
      }
    },
    total () {
      return this.presentPoints.reduce((sum, point) => sum + (point.value || 0), 0)
    },
    peak () {
      return this.presentPoints.reduce((best, point) => (!best || point.value > best.value ? point : best), null)
    },
    hourRows () {
      return this.points.slice().reverse().map((point) => ({
        ts: point.ts,
        value: point.value,
        rank: point.rank,
        devices: (point.devices || []).join(', ')
      }))
    },
    hourFields () {
      return [
        { key: 'ts', label: 'Collected (America/Bogota)', formatter: (value) => formatDate(value) },
        {
          key: 'value',
          label: this.metricInfo.label,
          class: 'text-right',
          formatter: (value) => (value === null ? 'not in top 25' : formatMetric(value, this.metricInfo.unit))
        },
        { key: 'rank', label: 'Rank', class: 'text-right', formatter: (value) => (value === null ? '—' : `#${value}`) },
        { key: 'devices', label: 'Reported by' }
      ]
    }
  },
  watch: {
    item () {
      this.loadTrend()
    },
    metricField () {
      this.loadTrend()
    }
  },
  mounted () {
    this.loadTrend()
  },
  methods: {
    formatDate,
    formatMetric,
    riskVariant,
    windowLabel,
    loadTrend () {
      if (!this.report || !this.item || !this.item.key) return
      const params = { report: this.report, key: this.item.key, hours: this.hours, metric: this.metricField }
      // Same identity as the ranking row: key plus the preset's group fields.
      this.preset.group_by.forEach((group) => {
        if (this.item[group.field]) params[group.field] = this.item[group.field]
      })
      if (this.device) params.device = this.device
      else if (this.site) params.site = this.site
      const query = Object.keys(params)
        .filter((key) => params[key])
        .map((key) => `${key}=${encodeURIComponent(params[key])}`)
        .join('&')
      this.isBusy = true
      this.loadError = ''
      axios
        .get(`${this.baseUrl}${this.basePath}/trend?${query}`)
        .then((response) => {
          const data = typeof response.data === 'string' ? JSON.parse(response.data) : response.data
          this.trend = data
          this.loadError = data.error || ''
          this.loaded = true
        })
        .catch((error) => {
          this.loadError = `Could not load the hourly trend: ${error.message}`
        })
        .finally(() => {
          this.isBusy = false
        })
    }
  }
}
</script>

<template>
  <div>
    <div class="d-flex align-items-center flex-wrap mb-2">
      <div class="mr-3 mb-1">
        <template v-for="group in preset.group_by">
          <b-badge
            v-if="item[group.field]"
            :key="group.field"
            variant="light"
            class="mr-1"
          >
            {{ group.label }}: {{ item[group.field] }}
          </b-badge>
        </template>
        <template v-for="extra in preset.extras">
          <b-badge
            v-if="item[extra.field] !== null && item[extra.field] !== undefined"
            :key="extra.field"
            :variant="extra.unit === 'risk' ? riskVariant(item[extra.field]) : 'light'"
            class="mr-1"
          >
            {{ extra.label }} {{ item[extra.field] }}
          </b-badge>
        </template>
        <template v-for="set in preset.sets">
          <b-badge
            v-for="value in item[set.field] || []"
            :key="`${set.field}-${value}`"
            variant="light"
            class="mr-1 font-weight-normal"
          >
            {{ value }}
          </b-badge>
        </template>
      </div>
      <b-form-select
        v-if="preset.metrics.length > 1"
        v-model="metricField"
        size="sm"
        class="ml-auto"
        style="max-width: 160px;"
        :options="preset.metrics.map((entry) => ({ value: entry.field, text: entry.label }))"
      />
    </div>

    <div class="row text-center mb-2">
      <div class="col-md-3 col-6">
        <div class="text-muted font-size-12">
          Total, {{ windowLabel(hours).toLowerCase() }}
        </div>
        <h4 class="my-1">
          {{ formatMetric(total, metricInfo.unit) }}
        </h4>
      </div>
      <div class="col-md-3 col-6">
        <div class="text-muted font-size-12">
          Hours in the top 25
        </div>
        <h4 class="my-1">
          {{ presentPoints.length }}<small class="text-muted">/{{ points.length }}</small>
        </h4>
      </div>
      <div class="col-md-3 col-6">
        <div class="text-muted font-size-12">
          Peak hour
        </div>
        <h4 class="my-1">
          {{ peak ? formatMetric(peak.value, metricInfo.unit) : '—' }}
        </h4>
        <div
          v-if="peak"
          class="text-muted font-size-11"
        >
          {{ formatDate(peak.ts) }}
        </div>
      </div>
      <div class="col-md-3 col-6">
        <div class="text-muted font-size-12">
          Best rank
        </div>
        <h4 class="my-1">
          {{ item.best_rank ? `#${item.best_rank}` : '—' }}
        </h4>
      </div>
    </div>

    <div
      v-if="loadError"
      class="text-danger py-3 text-center font-size-13"
    >
      {{ loadError }}
    </div>
    <div
      v-else-if="!loaded"
      class="text-muted py-4 text-center"
    >
      <b-spinner small />&nbsp;&nbsp;Loading hourly trend...
    </div>
    <div
      v-else-if="!points.length"
      class="text-muted py-4 text-center"
    >
      No hourly snapshots in this window.
    </div>
    <template v-else>
      <apexchart
        type="line"
        height="300"
        :series="series"
        :options="options"
      />
      <div class="text-muted font-size-12 mb-2">
        Each bar is one hourly report. A gap means the item was not in that hour's top 25, so its value is
        unknown rather than zero.
      </div>
      <div
        class="table-responsive"
        style="max-height: 320px;"
      >
        <b-table
          :items="hourRows"
          :fields="hourFields"
          small
          sticky-header="300px"
          :busy="isBusy"
        />
      </div>
    </template>
  </div>
</template>
